import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Lock, User, Phone, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

const LoginPage = () => {
  const { language } = useLanguage();
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });

  // If already logged in, redirect (use effect to avoid setState-in-render warning)
  useEffect(() => {
    if (user) {
      navigate('/account', { replace: true });
    }
  }, [user, navigate]);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(form.name, form.email, form.password, form.phone);
        toast.success(language === 'it' ? 'Registrazione completata!' : 'Registration complete!');
      } else {
        await login(form.email, form.password);
        toast.success(language === 'it' ? 'Accesso effettuato!' : 'Logged in!');
      }
      navigate('/account', { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const t = language === 'it';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div className="w-full max-w-md">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm mb-8 hover:text-[var(--brand-primary)] transition-colors" style={{ color: 'var(--text-muted)' }}>
          <ArrowLeft size={18} /> {t ? 'Indietro' : 'Back'}
        </button>

        <div className="dark-card p-8">
          <h1 className="text-2xl font-bold mb-2 text-center">
            {isRegister ? (t ? 'Crea Account' : 'Create Account') : (t ? 'Accedi' : 'Sign In')}
          </h1>
          <p className="text-sm text-center mb-6" style={{ color: 'var(--text-muted)' }}>
            {isRegister
              ? (t ? 'Registrati per gestire i tuoi ordini' : 'Register to manage your orders')
              : (t ? 'Accedi al tuo account ALLMX' : 'Sign in to your ALLMX account')}
          </p>

          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded border border-white/20 hover:bg-white/5 transition-colors mb-6"
            data-testid="google-login-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <span className="font-semibold text-sm">
              {t ? 'Continua con Google' : 'Continue with Google'}
            </span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px" style={{ background: 'var(--border-primary)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t ? 'oppure' : 'or'}</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-primary)' }} />
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t ? 'Nome e Cognome' : 'Full Name'}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  data-testid="register-name"
                />
              </div>
            )}

            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email"
                required
                className="w-full pl-10 pr-4 py-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                data-testid="login-email"
              />
            </div>

            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input
                type={showPw ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Password"
                required
                minLength={6}
                className="w-full pl-10 pr-10 py-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                data-testid="login-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {isRegister && (
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={t ? 'Telefono (opzionale)' : 'Phone (optional)'}
                  className="w-full pl-10 pr-4 py-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                  data-testid="register-phone"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 font-semibold"
              data-testid="submit-btn"
            >
              {loading ? '...' : (isRegister ? (t ? 'Registrati' : 'Register') : (t ? 'Accedi' : 'Sign In'))}
            </button>
          </form>

          {/* Toggle */}
          <p className="text-sm text-center mt-6" style={{ color: 'var(--text-muted)' }}>
            {isRegister ? (t ? 'Hai già un account?' : 'Already have an account?') : (t ? 'Non hai un account?' : "Don't have an account?")}
            {' '}
            <button onClick={() => setIsRegister(!isRegister)} className="text-[var(--brand-primary)] font-semibold hover:underline" data-testid="toggle-auth">
              {isRegister ? (t ? 'Accedi' : 'Sign In') : (t ? 'Registrati' : 'Register')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
